import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { IQuestionnaireQuestion } from 'src/app/interfaces/Questionnaire/IQuestionnaire';
import {
  joinMrcGrades,
  MRC_MUSCLES,
  splitMrcGrades,
} from 'src/app/utils/mrc-grades';

type MrcSide = 'left' | 'right';

// Titles separate the instruction with "||" or "‖".
const MRC_TITLE_SEPARATOR = /\|\||‖/;

// Each muscle is graded on both sides, left first, as it is saved: "4,5".
const MRC_SIDES: MrcSide[] = ['left', 'right'];

// The sum of all 12 grades, out of 60. The backend never reads it.
const MRC_TOTAL = 'question685';
const MRC_MAX_TOTAL = 60;

// A grade is one whole number from 0 to 5.
const MRC_GRADE_PATTERN = /^[0-5]$/;

// Blank, or two grades from 0 to 5 where either side may still be blank.
function gradesValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '').trim();
  if (!value) return null;

  const grades = value.split(',').map((grade) => grade.trim());
  return grades.length === 2 &&
    grades.every((grade) => !grade || MRC_GRADE_PATTERN.test(grade))
    ? null
    : { grades: true };
}

@Component({
  selector: 'app-mrc-questions',
  templateUrl: './mrc-questions.component.html',
  styleUrl: './mrc-questions.component.css',
  standalone: false,
})
export class MrcQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  @Input() formGroup!: FormGroup;

  readonly sides = MRC_SIDES;
  readonly maxTotal = MRC_MAX_TOTAL;
  // The grade guide: 0 to 2 in the left column, 3 to 5 in the right.
  readonly grades = [0, 1, 2, 3, 4, 5];
  headerElement?: IQuestionnaireQuestion;
  muscleElements: IQuestionnaireQuestion[] = [];
  totalElement?: IQuestionnaireQuestion;
  // The Left and Right boxes of each muscle; the form holds the joined answer.
  private sideControls: Record<
    string,
    Record<MrcSide, FormControl<string | null>>
  > = {};
  private subs = new Subscription();

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.headerElement = this.elements.find((el) => el.type === 'header');
    this.muscleElements = MRC_MUSCLES.map((muscle) =>
      this.elements.find((el) => el.name === muscle),
    ).filter((el): el is IQuestionnaireQuestion => !!el);
    this.totalElement = this.elements.find((el) => el.name === MRC_TOTAL);

    for (const muscle of MRC_MUSCLES) {
      const control = this.formGroup.get(muscle);
      if (!control) continue;

      control.addValidators(gradesValidator);
      control.updateValueAndValidity({ emitEvent: false });

      const [left, right] = splitMrcGrades(control.value);
      this.sideControls[muscle] = {
        left: new FormControl(left),
        right: new FormControl(right),
      };
      for (const side of MRC_SIDES) {
        this.subs.add(
          this.sideControls[muscle][side].valueChanges.subscribe(() =>
            this.onSideChange(muscle, side),
          ),
        );
      }
    }

    // Loading and Revert set the joined answers; the boxes follow them.
    this.subs.add(
      this.formGroup.valueChanges.subscribe(() => this.syncFromForm()),
    );
    this.syncFromForm();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  getQuestionTitle(title?: string): string {
    return title?.split(MRC_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getSideControl(muscle: string, side: MrcSide): FormControl<string | null> {
    return this.sideControls[muscle][side];
  }

  isInvalid(element: IQuestionnaireQuestion): boolean {
    return !!this.formGroup.get(element.name)?.invalid;
  }

  getTotalDisplay(): string {
    const value = this.formGroup.get(MRC_TOTAL)?.value;
    return value === null || value === undefined || value === ''
      ? '0'
      : String(value);
  }

  // Only one grade from 0 to 5 can be typed or pasted into a box.
  private onSideChange(muscle: string, side: MrcSide): void {
    const box = this.sideControls[muscle][side];
    const value = String(box.value ?? '');
    const grade = value.replace(/[^0-5]/g, '').slice(0, 1);
    if (grade !== value) {
      box.setValue(grade, { emitEvent: false });
    }

    const { left, right } = this.sideControls[muscle];
    this.formGroup
      .get(muscle)
      ?.setValue(joinMrcGrades(left.value ?? '', right.value ?? ''));
  }

  private syncFromForm(): void {
    for (const muscle of MRC_MUSCLES) {
      const control = this.formGroup.get(muscle);
      const boxes = this.sideControls[muscle];
      if (!control || !boxes) continue;

      const grades = splitMrcGrades(control.value);
      MRC_SIDES.forEach((side, index) => {
        const box = boxes[side];
        if (box.value !== grades[index]) {
          box.setValue(grades[index], { emitEvent: false });
        }
        // A read-only form keeps its boxes locked too.
        if (control.disabled && box.enabled) {
          box.disable({ emitEvent: false });
        } else if (control.enabled && box.disabled) {
          box.enable({ emitEvent: false });
        }
      });
    }

    this.setTotal();
    this.cdr.detectChanges();
  }

  // Live sum of the grades entered so far, or empty while none is, so an
  // untouched form saves no total.
  private setTotal(): void {
    const control = this.formGroup.get(MRC_TOTAL);
    if (!control) return;

    const grades = MRC_MUSCLES.flatMap((muscle) =>
      splitMrcGrades(this.formGroup.get(muscle)?.value),
    ).filter((grade) => MRC_GRADE_PATTERN.test(grade));
    const total = grades.reduce((sum, grade) => sum + Number(grade), 0);

    control.setValue(grades.length ? String(total) : '', {
      emitEvent: false,
    });
    control.disable({ emitEvent: false });
  }
}
